import PropTypes from 'prop-types';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import SvgIcon from './SvgIcon';
import { createDevLogger } from '../utils/devLog';
import {
    hasMeasurableTarget,
    isLetterPosition,
    letterFromPosition,
    POSITION_TARGETS,
} from '../../constants/pointerCursor';

const logger = createDevLogger('PointerCursor');

// The pointer graphic's tip sits this far right of its container's left edge,
// level with the container's top edge.
const TIP_OFFSET_X = 19;

const findTarget = (container, position) => {
    if (isLetterPosition(position)) {
        const letter = letterFromPosition(position);
        return Array.from(container.querySelectorAll('[data-letter]')).find(
            key => key.dataset.letter === letter,
        );
    }

    return container.querySelector(POSITION_TARGETS[position]);
};

// Measures where the target actually sits rather than relying on coordinates
// hardcoded per language and per zoom level, which go stale whenever the
// keyboard layout, zoom level or viewport changes (SZM-533, SZM-1220).
const offsetToTarget = (pointer, container, target) => {
    const targetBox = target.getBoundingClientRect();
    if (!targetBox.width || !targetBox.height) {
        return null;
    }

    const containerBox = container.getBoundingClientRect();
    const tipX = targetBox.left + targetBox.width / 2;
    const tipY = targetBox.top + targetBox.height / 2;

    return {
        right: containerBox.right - tipX - (pointer.offsetWidth - TIP_OFFSET_X),
        bottom: containerBox.bottom - tipY - pointer.offsetHeight,
    };
};

const PointerCursor = props => {
    const pointer = useRef(null);
    const [offset, setOffset] = useState(null);
    const warnedPosition = useRef(null);

    const updateOffset = useCallback(() => {
        if (!pointer.current) {
            return;
        }

        if (!hasMeasurableTarget(props.position)) {
            setOffset(null);
            return;
        }

        const container = pointer.current.offsetParent;
        if (!container) {
            return;
        }

        const target = findTarget(container, props.position);
        if (!target) {
            // The position promised an element, so nothing matching it means
            // the selector or the data-letter hook drifted from the markup,
            // leaving the pointer frozen. Warn once per position rather than
            // on every remeasure, which would bury the signal.
            if (warnedPosition.current !== props.position) {
                warnedPosition.current = props.position;
                logger.warn(`no element matches position "${props.position}"`);
            }
            return;
        }

        warnedPosition.current = null;
        const next = offsetToTarget(pointer.current, container, target);
        // Hold the last offset while the target is on screen but not laid out
        // yet, so the pointer stays put instead of jumping away.
        if (next) {
            setOffset(next);
        }
    }, [props.position]);

    useLayoutEffect(updateOffset, [updateOffset]);

    useLayoutEffect(() => {
        window.addEventListener('resize', updateOffset);
        return () => window.removeEventListener('resize', updateOffset);
    }, [updateOffset]);

    const positionClass = props.position ? `point-${props.position}` : '';

    return (
        <div
            className={`pointer-cursor-container ${positionClass}`}
            ref={pointer}
            style={
                offset ? { right: `${offset.right}px`, bottom: `${offset.bottom}px` } : undefined
            }
        >
            <SvgIcon
                id="pointer"
                ariaLabelledBy="Cursor Pointer"
                class="lantern-svg"
                ariaDescribedBy="Cursor Pointer"
            />
            <span className={`pointer-cursor-click ${props.clicked ? ' ripple' : ''}`} />
        </div>
    );
};

PointerCursor.propTypes = {
    clicked: PropTypes.bool,
    position: PropTypes.string,
};

PointerCursor.defaultProps = {
    clicked: false,
    position: undefined,
};

export default PointerCursor;
